import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { adminApi } from "@/lib/adminApi";
import { fmtDate, fmtMoney, statusBadge } from "@/pages/admin/util";
import { Loader2, RefreshCw, Send, UserPlus, Flame, FileText, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import ReportEditor from "@/pages/admin/ReportEditor";
import ContractorBadgeForName from "@/components/admin/ContractorBadgeForName";

const STATUSES = ["", "NEW", "REVIEWING", "AWAITING_INFO", "ADVISED", "CLOSED"];

export default function QuoteReviews() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [reportId, setReportId] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.get("/quote-reviews", { params: statusFilter ? { status: statusFilter } : {} });
      setItems(data.items);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  // Deep-link from admin notification email: ?case_id=<uuid> goes straight to the workspace.
  useEffect(() => {
    const cid = searchParams.get("case_id");
    if (cid) {
      searchParams.delete("case_id");
      setSearchParams(searchParams, { replace: true });
      navigate(`/adminpanel/workspace/${cid}`);
    }
  }, [searchParams, setSearchParams, navigate]);

  async function sendLink(id) {
    if (!window.confirm("Send membership link to this lead?")) return;
    try {
      await adminApi.post(`/quote-reviews/${id}/send-membership-link`);
      toast.success("Membership link sent");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Send failed");
    }
  }

  async function convert(id) {
    if (!window.confirm("Manually convert this lead to an ACTIVE member?\nUse this only if payment was taken outside Stripe or as a manual override.")) return;
    try {
      await adminApi.post(`/quote-reviews/${id}/convert`);
      toast.success("Converted to active member");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Conversion failed");
    }
  }

  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="admin-quote-reviews-page">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-7">
        <div>
          <p className="text-[10.5px] tracking-[0.32em] uppercase text-[#B88A2A] font-semibold">Revenue Engine</p>
          <h1 className="font-display text-[32px] lg:text-[40px] leading-[1.05] tracking-tight text-[#071A33] mt-2">Quote Reviews</h1>
          <p className="text-sm text-[#475569] mt-2 max-w-2xl">Review, advise and convert into membership.</p>
        </div>
        <div className="flex items-center gap-3">
          <select className="field-input h-10" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="qr-status-filter">
            {STATUSES.map((s) => <option key={s} value={s}>{s || "All statuses"}</option>)}
          </select>
          <button onClick={load} className="btn-outline h-10 py-0 px-4" data-testid="qr-refresh"><RefreshCw size={14}/></button>
        </div>
      </div>

      <div className="bg-white border border-[#E2DCD0] rounded-xl overflow-x-auto shadow-[0_1px_0_rgba(7,26,51,0.04)]">
        <table className="w-full text-sm" data-testid="quote-reviews-table">
          <thead>
            <tr className="bg-[#F7F4ED] text-left text-[#475569] text-xs uppercase tracking-[0.15em]">
              <th className="px-4 py-3">Name</th>
              <th className="px-4 py-3">Email</th>
              <th className="px-4 py-3">Cost / Client</th>
              <th className="px-4 py-3">Contractor</th>
              <th className="px-4 py-3">Concern</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Member</th>
              <th className="px-4 py-3">Conversion</th>
              <th className="px-4 py-3">Files</th>
              <th className="px-4 py-3">Report</th>
              <th className="px-4 py-3">Created</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={12} className="px-4 py-8 text-center text-[#475569]"><Loader2 className="animate-spin inline" size={16}/></td></tr>}
            {!loading && items.length === 0 && <tr><td colSpan={12} className="px-4 py-8 text-center text-[#475569]">No quote reviews yet.</td></tr>}
            {items.map((c) => (
              <tr key={c.id} className="border-t border-[#E2DCD0] hover:bg-[#F7F4ED]/40">
                <td className="px-4 py-3 font-medium text-[#071A33]">
                  <span className="inline-flex items-center gap-2">
                    {c.full_name}
                    {c.high_value && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-medium text-[#DC2626] bg-[#DC2626]/10 px-1.5 py-0.5 uppercase tracking-[0.08em]" data-testid="high-value-badge">
                        <Flame size={10}/> High value
                      </span>
                    )}
                  </span>
                </td>
                <td className="px-4 py-3 text-[#475569]">{c.email}</td>
                <td className="px-4 py-3 text-[#071A33]" data-testid={`qr-pricing-${c.id}`}>
                  <p className="text-[10px] tracking-[0.12em] uppercase text-[#475569]">Cost</p>
                  <p className="tabular-nums">{fmtMoney(c.contractor_cost_ex_vat ?? c.quote_value)}</p>
                  {((c.client_price_ex_vat ?? c.quote_value) !== (c.contractor_cost_ex_vat ?? c.quote_value)) && (
                    <>
                      <p className="text-[10px] tracking-[0.12em] uppercase text-[#064E2E] mt-1">Client{c.manual_price_override ? " · manual" : c.markup_enabled ? ` · +${c.markup_percentage || 0}%` : ""}</p>
                      <p className="tabular-nums font-medium text-[#064E2E]">{fmtMoney(c.client_price_ex_vat ?? c.quote_value)}</p>
                    </>
                  )}
                </td>
                <td className="px-4 py-3 text-[#475569]">
                  {c.contractor_name ? (
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span>{c.contractor_name}</span>
                      <ContractorBadgeForName name={c.contractor_name} size="sm"/>
                    </div>
                  ) : "—"}
                </td>
                <td className="px-4 py-3 text-[#475569]">{c.issue_type || "—"}</td>
                <td className="px-4 py-3">{statusBadge(c.status)}</td>
                <td className="px-4 py-3">{c.member_status ? statusBadge(c.member_status) : <span className="text-xs text-[#475569]">—</span>}</td>
                <td className="px-4 py-3">
                  {c.conversion_status === "CONVERTED" && <span className="text-[10px] text-[#064E2E] bg-[#064E2E]/10 px-1.5 py-0.5 font-medium uppercase tracking-[0.08em]">Converted</span>}
                  {c.conversion_status === "LINK_SENT" && <span className="text-[10px] text-[#B88A2A] bg-[#B88A2A]/15 px-1.5 py-0.5 font-medium uppercase tracking-[0.08em]">Link sent</span>}
                  {(!c.conversion_status || c.conversion_status === "NONE") && <span className="text-[10px] text-[#475569]">—</span>}
                </td>
                <td className="px-4 py-3 text-[#475569]">{(c.uploaded_files || []).length}</td>
                <td className="px-4 py-3">
                  {c.report_status === "published"
                    ? <span className="text-[10px] text-[#064E2E] bg-[#064E2E]/10 px-1.5 py-0.5 font-medium uppercase tracking-[0.08em]">Published</span>
                    : c.report_status === "draft"
                      ? <span className="text-[10px] text-[#B88A2A] bg-[#B88A2A]/15 px-1.5 py-0.5 font-medium uppercase tracking-[0.08em]">Draft</span>
                      : <span className="text-[10px] text-[#475569]">—</span>}
                </td>
                <td className="px-4 py-3 text-[#475569]">{fmtDate(c.created_at, true)}</td>
                <td className="px-4 py-3 text-right">
                  <div className="flex items-center justify-end gap-1">
                    <button onClick={() => setReportId(c.id)} className="text-xs px-2.5 py-1.5 border border-[#B88A2A] text-[#B88A2A] hover:bg-[#B88A2A] hover:text-white inline-flex items-center gap-1" data-testid={`qr-report-${c.id}`} title="Write / publish the Quote Review Report">
                      <FileText size={12}/> Report
                    </button>
                    <button onClick={() => sendLink(c.id)} className="text-xs px-2.5 py-1.5 bg-[#064E2E] text-white hover:bg-[#053A22] inline-flex items-center gap-1" data-testid={`qr-send-link-${c.id}`} title="Email membership payment link">
                      <Send size={12}/> Send Link
                    </button>
                    <button onClick={() => convert(c.id)} className="text-xs px-2.5 py-1.5 border border-[#064E2E] text-[#064E2E] hover:bg-[#064E2E] hover:text-white inline-flex items-center gap-1" data-testid={`qr-convert-${c.id}`} title="Manual conversion (override)">
                      <UserPlus size={12}/> Convert
                    </button>
                    <Link to={`/adminpanel/workspace/${c.id}`} className="text-xs px-2.5 py-1.5 bg-[#0F766E] text-white hover:bg-[#0a5b55] inline-flex items-center gap-1 font-medium rounded" data-testid={`qr-view-${c.id}`}>
                      Open workspace <ExternalLink size={11}/>
                    </Link>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ReportEditor caseId={reportId} onClose={() => setReportId(null)} onSaved={load}/>
    </div>
  );
}
